import { useCallback, useEffect, useState } from "react";
import { Ban, Check, Copy, MonitorPlay, Plus, Trash2 } from "lucide-react";
import { api, errorText } from "../lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");
const ONLINE_MS = 2 * 60 * 1000;

const seen = (iso) => {
  if (!iso) return { text: "never connected", online: false };
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < ONLINE_MS) return { text: "online now", online: true };
  const min = Math.round(ms / 60000);
  if (min < 60) return { text: `seen ${min} min ago`, online: false };
  if (min < 48 * 60) return { text: `seen ${Math.round(min / 60)} h ago`, online: false };
  return { text: `seen ${when(iso)}`, online: false };
};

const NewLink = ({ created, onDone }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
    } catch {
      document.getElementById("display-new-link")?.select();
    }
  };
  return (
    <div className="rounded-lg border border-live/40 bg-live/5 p-4 space-y-2" data-testid="display-new-link-box">
      <div className="text-sm font-semibold text-ink">Display link for “{created.label}” – shown only once, copy it now</div>
      <div className="flex gap-2">
        <input id="display-new-link" data-testid="display-new-link" readOnly value={created.url} onFocus={(e) => e.target.select()} className="num flex-1 min-w-0 rounded-lg bg-panel border border-slate-300 px-3 py-2 text-xs text-ink" />
        <button type="button" data-testid="display-copy-button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-lg bg-ink text-page px-3 py-2 text-xs font-semibold hover:bg-ink/80 transition-colors shrink-0">
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="text-xs text-slate-500">Send exactly this link to the screen (Videri remote / TV browser). It opens the board without any sign-in. Anyone with the link can see the board – revoke it here if it leaks. Add <code className="num">&amp;theme=dark</code> to any display link for the dark board.</p>
      <button type="button" data-testid="display-new-link-done" onClick={onDone} className="text-xs text-slate-500 underline hover:text-ink">Done</button>
    </div>
  );
};

const Row = ({ d, onRevoke, onDelete }) => {
  const s = seen(d.last_seen_at);
  const revoked = !!d.revoked_at;
  return (
    <div data-testid={`display-row-${d.id}`} className={`px-4 py-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1 ${revoked ? "opacity-60" : ""}`}>
      <MonitorPlay className="w-4 h-4 text-slate-400 shrink-0" />
      <div className="flex-1 min-w-[160px]">
        <div className="font-semibold text-ink truncate">{d.label}</div>
        <div className="text-xs text-slate-500">Created {when(d.created_at)}</div>
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-500 min-w-[150px]" data-testid={`display-last-seen-${d.id}`}>
        <span className={`w-2 h-2 rounded-full ${s.online && !revoked ? "bg-live live-dot" : "bg-slate-300"}`} />
        {s.text}
      </div>
      <span data-testid={`display-status-${d.id}`} className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${revoked ? "bg-slate-100 text-slate-500" : "bg-live/10 text-live"}`}>
        {revoked ? "Revoked" : "Active"}
      </span>
      {revoked ? (
        <button type="button" data-testid={`display-delete-${d.id}`} onClick={() => onDelete(d)} title="Remove from the list" className="inline-flex items-center gap-1 rounded-full border border-panel-border px-3 py-1 text-xs text-slate-600 hover:border-slate-400 hover:text-ink transition-colors">
          <Trash2 className="w-3.5 h-3.5" /> Remove
        </button>
      ) : (
        <button type="button" data-testid={`display-revoke-${d.id}`} onClick={() => onRevoke(d)} title="The screen will show the sign-in page within 30 s" className="inline-flex items-center gap-1 rounded-full border border-brand/40 px-3 py-1 text-xs text-brand hover:bg-brand hover:text-white transition-colors">
          <Ban className="w-3.5 h-3.5" /> Revoke
        </button>
      )}
    </div>
  );
};

// Admin: display links for TV / Videri screens that cannot sign in with Google
export const DisplayKeys = () => {
  const [items, setItems] = useState(null);
  const [label, setLabel] = useState("");
  const [darkTheme, setDarkTheme] = useState(false);
  const [created, setCreated] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/displays");
      setItems(r.data.displays);
    } catch (e) {
      setError(errorText(e));
    }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/admin/displays", { label: label.trim() });
      setCreated({ label: r.data.label, url: `${window.location.origin}/?display=${encodeURIComponent(r.data.key)}${darkTheme ? "&theme=dark" : ""}` });
      setLabel("");
      load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const revoke = async (d) => {
    if (!window.confirm(`Revoke the display link “${d.label}”? That screen will show the sign-in page within 30 seconds.`)) return;
    try {
      await api.post(`/admin/displays/${d.id}/revoke`);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };
  const remove = async (d) => {
    try {
      await api.delete(`/admin/displays/${d.id}`);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <section data-testid="display-keys-section" className="space-y-3">
      <div>
        <div className="eyebrow">Display screens · TV / Videri</div>
        <p className="text-xs text-slate-500 mt-1 max-w-3xl" data-testid="display-keys-help">
          Signage screens cannot complete a Google sign-in (no keyboard, embedded browser). Create a display link per screen and send that link to the screen – it opens the board directly. Each link is tied to the screen name shown here; revoke it any time.
        </p>
      </div>
      <form onSubmit={create} className="panel p-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
        <input
          data-testid="display-label-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={80}
          placeholder="Screen name, e.g. Office Zagreb – Videri"
          className="flex-1 min-w-[220px] rounded-lg bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition"
        />
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 select-none cursor-pointer" title="Adds &theme=dark to the link – the screen opens the board in the dark theme">
          <input type="checkbox" data-testid="display-dark-theme" checked={darkTheme} onChange={(e) => setDarkTheme(e.target.checked)} className="accent-[#DC2A27] w-4 h-4" />
          Dark theme on this screen
        </label>
        <button type="submit" data-testid="display-create-button" disabled={busy || !label.trim()} className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand hover:bg-[#c42320] disabled:opacity-50 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors">
          <Plus className="w-4 h-4" /> Create display link
        </button>
      </form>
      {created && <NewLink created={created} onDone={() => setCreated(null)} />}
      {error && <div className="panel p-3 text-brand text-sm" data-testid="display-keys-error">{error}</div>}
      <div className="panel divide-y divide-panel-border" data-testid="display-keys-list">
        {items && !items.length && <div className="p-4 text-sm text-slate-500" data-testid="display-keys-empty">No display links yet.</div>}
        {(items || []).map((d) => <Row key={d.id} d={d} onRevoke={revoke} onDelete={remove} />)}
      </div>
    </section>
  );
};
